<!--
  The filter bar over `entries_list`'s own query parameters, minus free-text search (that lives in
  the page header, TransactionSearch.vue): a kind toggle, then a chip per remaining filter. A chip
  is its own echo - outlined while unset, iris once it narrows the list, removable in place.
  Merchant, source and amount are rarely used and sit behind "More filters" until one of them is set.
-->
<script setup lang="ts">
import { formatDate, numberFormatFor, parseLocalDate } from '@taler/domain'
import {
  DecimalInput,
  formatAmountDisplay,
  KindIcon,
  Select,
  SearchableSelect,
  type SelectOption,
} from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  AccountPicker,
  MerchantCombobox,
  type Frecency,
  type Merchant,
  type TagOption,
} from '@/features/entries'
import { ENTRY_SOURCES, type DefiniteEntryKind, type DefiniteEntrySource } from '../internal/query-params.ts'
import type { PostableAccountOption } from '../internal/entry-detail.ts'
import type {
  TransactionFilterPatch,
  TransactionFilterQuery,
} from '../composables/use-transaction-filters.ts'
import FilterChip from '@/features/transactions/components/FilterChip.vue'
import FilterPopover from '@/features/transactions/components/FilterPopover.vue'

const props = defineProps<{
  filters: TransactionFilterQuery
  accountOptions: readonly PostableAccountOption[]
  canViewMerchants: boolean
  merchants: readonly Merchant[]
  merchantFrecency: ReadonlyMap<string, Frecency>
  tagOptions: readonly TagOption[]
}>()

const emit = defineEmits<{
  filter: [patch: TransactionFilterPatch]
  clear: []
  /** The Partner combobox's typed text, forwarded so the caller can search the full
   * merchant directory server-side - see `useEntryMerchants`' own `search`. */
  merchantSearch: [string]
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const numberFormat = computed(() => numberFormatFor(currentLocale.value))

// A reka Select left uncontrolled keeps its last pick, so "no source" needs a real value.
const ALL = 'ALL' as const

// Expense first: the kind most of a household's journal is.
const KIND_TOGGLE_ORDER: readonly DefiniteEntryKind[] = ['EXPENSE', 'INCOME', 'TRANSFER']
const KIND_CHIP_CLASS =
  'flex h-[var(--density-control)] items-center gap-[var(--space-tight)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)] transition-colors duration-[var(--motion-fast)] hover:bg-[color:var(--color-surface-raised)] aria-pressed:border-[color:color-mix(in_srgb,var(--color-accent)_40%,transparent)] aria-pressed:bg-[color:color-mix(in_srgb,var(--color-accent)_10%,transparent)] aria-pressed:font-semibold aria-pressed:text-[color:var(--color-accent)]'

const sourceOptions = computed<SelectOption<DefiniteEntrySource | typeof ALL>[]>(() => [
  { value: ALL, label: t('transactions.filters.allSources') },
  ...ENTRY_SOURCES.map((source) => ({ value: source, label: t(`transactions.source.${source}`) })),
])
const tagSelectOptions = computed(() =>
  props.tagOptions.map((option) => ({ value: option.tag, label: option.tag })),
)

function onDateInput(key: 'from' | 'until', event: Event): void {
  const value = (event.target as HTMLInputElement).value
  emit('filter', { [key]: value === '' ? undefined : value })
}
function onAmountChange(key: 'minAmount' | 'maxAmount', value: string): void {
  emit('filter', { [key]: value === '' ? undefined : value })
}
function onKindChange(kind: DefiniteEntryKind | undefined): void {
  emit('filter', { kind })
}
function onSourceChange(value: DefiniteEntrySource | typeof ALL): void {
  emit('filter', { source: value === ALL ? undefined : value })
}
function onAccountPicked(value: string | null, close: () => void): void {
  if (value === null) return
  emit('filter', { accountId: value })
  close()
}
function onTagsChange(value: string | readonly string[]): void {
  const tags = Array.isArray(value) ? value : []
  emit('filter', { tags: tags.length > 0 ? tags : undefined })
}
function onMerchantSelect(merchant: Merchant, close: () => void): void {
  emit('filter', { merchantId: merchant.id })
  close()
}
function onMerchantModelValue(value: string | undefined): void {
  if (value === undefined) emit('filter', { merchantId: undefined })
}

function labelled(name: string, value: string | undefined): string {
  return value === undefined ? name : `${name}: ${value}`
}
function removeLabel(label: string): string {
  return t('transactions.filters.removeFilter', { label })
}

const displayDate = (iso: string): string => formatDate(parseLocalDate(iso), currentLocale.value)
const displayAmount = (clean: string): string =>
  formatAmountDisplay(clean, { ...numberFormat.value, decimalDigits: 4, allowNegative: false })

const dateValue = computed(() => {
  const { from, until } = props.filters
  if (from !== undefined && until !== undefined) return `${displayDate(from)} – ${displayDate(until)}`
  if (from !== undefined) return `≥ ${displayDate(from)}`
  if (until !== undefined) return `≤ ${displayDate(until)}`
  return undefined
})
const accountValue = computed(() => {
  const id = props.filters.accountId
  if (id === undefined) return undefined
  return props.accountOptions.find((account) => account.id === id)?.label ?? id
})
const tagsValue = computed(() => {
  const tags = props.filters.tags
  return tags === undefined || tags.length === 0
    ? undefined
    : tags.join(t('transactions.filters.tagsSeparator'))
})
const selectedMerchantName = computed(
  () => props.merchants.find((merchant) => merchant.id === props.filters.merchantId)?.name,
)
const merchantValue = computed(() =>
  props.filters.merchantId === undefined
    ? undefined
    : (selectedMerchantName.value ?? props.filters.merchantId),
)
const sourceValue = computed(() =>
  props.filters.source === undefined ? undefined : t(`transactions.source.${props.filters.source}`),
)
const amountValue = computed(() => {
  const { minAmount, maxAmount } = props.filters
  if (minAmount !== undefined && maxAmount !== undefined)
    return `${displayAmount(minAmount)} – ${displayAmount(maxAmount)}`
  if (minAmount !== undefined) return `≥ ${displayAmount(minAmount)}`
  if (maxAmount !== undefined) return `≤ ${displayAmount(maxAmount)}`
  return undefined
})

const dateLabel = computed(() => labelled(t('transactions.filters.date'), dateValue.value))
const accountLabel = computed(() => labelled(t('transactions.filters.account'), accountValue.value))
const tagsLabel = computed(() => labelled(t('transactions.filters.tags'), tagsValue.value))
const merchantLabel = computed(() => labelled(t('transactions.filters.merchant'), merchantValue.value))
const sourceLabel = computed(() => labelled(t('transactions.filters.source'), sourceValue.value))
const amountLabel = computed(() => labelled(t('transactions.filters.amount'), amountValue.value))

const moreOpen = ref(false)
const hasAnyFilter = computed(() => Object.keys(props.filters).length > 0)
</script>

<template>
  <div
    role="search"
    :aria-label="t('transactions.filters.region')"
    class="flex flex-col gap-[var(--space-inline)]"
  >
    <div
      role="group"
      :aria-label="t('transactions.filters.kind')"
      class="flex flex-wrap items-center gap-[var(--space-inline)]"
    >
      <button
        type="button"
        :aria-pressed="filters.kind === undefined"
        :class="KIND_CHIP_CLASS"
        @click="onKindChange(undefined)"
      >
        {{ t('transactions.filters.anyKind') }}
      </button>
      <button
        v-for="kind in KIND_TOGGLE_ORDER"
        :key="kind"
        type="button"
        :aria-pressed="filters.kind === kind"
        :class="KIND_CHIP_CLASS"
        @click="onKindChange(kind)"
      >
        <span aria-hidden="true"><KindIcon :kind="kind" label="" /></span>
        {{ t(`transactions.${kind.toLowerCase()}`) }}
      </button>
    </div>

    <div class="flex flex-wrap items-center gap-[var(--space-inline)]">
      <FilterChip
        :active="dateValue !== undefined"
        :remove-label="removeLabel(dateLabel)"
        @remove="emit('filter', { from: undefined, until: undefined })"
      >
        <FilterPopover :label="dateLabel">
          <template #trigger>
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              stroke-width="1.7"
              class="shrink-0"
            >
              <path d="M2.8 3.6h10.4v9.6H2.8zM2.8 6.6h10.4M5.4 2v3M10.6 2v3" />
            </svg>
            <span class="truncate">{{ dateValue ?? t('transactions.filters.date') }}</span>
          </template>
          <label
            class="flex flex-col gap-[var(--space-tight)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >
            {{ t('transactions.filters.from') }}
            <input
              type="date"
              :value="filters.from ?? ''"
              class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
              @change="onDateInput('from', $event)"
            />
          </label>
          <label
            class="flex flex-col gap-[var(--space-tight)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >
            {{ t('transactions.filters.until') }}
            <input
              type="date"
              :value="filters.until ?? ''"
              class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
              @change="onDateInput('until', $event)"
            />
          </label>
        </FilterPopover>
      </FilterChip>

      <FilterChip
        :active="accountValue !== undefined"
        :remove-label="removeLabel(accountLabel)"
        @remove="emit('filter', { accountId: undefined })"
      >
        <FilterPopover :label="accountLabel">
          <template #trigger>
            <span class="truncate">{{ accountLabel }}</span>
          </template>
          <template #default="{ close }">
            <AccountPicker
              id="transaction-filter-account"
              :label="t('transactions.filters.account')"
              :model-value="filters.accountId"
              include-system
              include-contacts
              @update:model-value="(value) => onAccountPicked(value, close)"
            />
          </template>
        </FilterPopover>
      </FilterChip>

      <FilterChip
        :active="tagsValue !== undefined"
        :remove-label="removeLabel(tagsLabel)"
        @remove="emit('filter', { tags: undefined })"
      >
        <SearchableSelect
          variant="chip"
          multiple
          :model-value="filters.tags ?? []"
          :options="tagSelectOptions"
          :trigger-label="t('transactions.filters.tags')"
          :search-label="t('transactions.filters.searchTags')"
          :empty-text="t('transactions.filters.noTagsFound')"
          :aria-label="labelled(t('transactions.filters.tagsHint'), tagsValue)"
          @update:model-value="onTagsChange"
        >
          <template #trigger>
            <span class="truncate">{{ tagsLabel }}</span>
          </template>
        </SearchableSelect>
      </FilterChip>

      <span id="transaction-more-filters" class="contents">
        <FilterChip
          v-if="canViewMerchants && (moreOpen || merchantValue !== undefined)"
          :active="merchantValue !== undefined"
          :remove-label="removeLabel(merchantLabel)"
          @remove="emit('filter', { merchantId: undefined })"
        >
          <FilterPopover :label="merchantLabel">
            <template #trigger>
              <span class="truncate">{{ merchantLabel }}</span>
            </template>
            <template #default="{ close }">
              <MerchantCombobox
                id="transaction-filter-merchant"
                :label="t('transactions.filters.merchant')"
                :model-value="selectedMerchantName"
                :merchants="merchants"
                :frecency="merchantFrecency"
                @update:model-value="onMerchantModelValue"
                @select="onMerchantSelect($event, close)"
                @query="emit('merchantSearch', $event)"
              />
            </template>
          </FilterPopover>
        </FilterChip>

        <FilterChip
          v-if="moreOpen || sourceValue !== undefined"
          :active="sourceValue !== undefined"
          :remove-label="removeLabel(sourceLabel)"
          @remove="emit('filter', { source: undefined })"
        >
          <Select
            variant="chip"
            :model-value="filters.source ?? ALL"
            :options="sourceOptions"
            :aria-label="sourceLabel"
            @update:model-value="onSourceChange"
          >
            <template #trigger>
              <span class="truncate">{{ sourceLabel }}</span>
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" class="shrink-0">
                <path d="M4 6.5 8 10.5 12 6.5" fill="none" stroke="currentColor" stroke-width="1.6" />
              </svg>
            </template>
          </Select>
        </FilterChip>

        <FilterChip
          v-if="moreOpen || amountValue !== undefined"
          :active="amountValue !== undefined"
          :remove-label="removeLabel(amountLabel)"
          @remove="emit('filter', { minAmount: undefined, maxAmount: undefined })"
        >
          <FilterPopover :label="amountLabel">
            <template #trigger>
              <span class="truncate">{{ amountLabel }}</span>
            </template>
            <div class="flex gap-[var(--space-inline)]">
              <label
                class="flex w-32 flex-col gap-[var(--space-tight)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
              >
                {{ t('transactions.filters.minAmount') }}
                <DecimalInput
                  :model-value="filters.minAmount ?? ''"
                  :decimal-digits="4"
                  :group-separator="numberFormat.groupSeparator"
                  :decimal-separator="numberFormat.decimalSeparator"
                  :show-total="false"
                  @update:model-value="(value) => onAmountChange('minAmount', value)"
                />
              </label>
              <label
                class="flex w-32 flex-col gap-[var(--space-tight)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
              >
                {{ t('transactions.filters.maxAmount') }}
                <DecimalInput
                  :model-value="filters.maxAmount ?? ''"
                  :decimal-digits="4"
                  :group-separator="numberFormat.groupSeparator"
                  :decimal-separator="numberFormat.decimalSeparator"
                  :show-total="false"
                  @update:model-value="(value) => onAmountChange('maxAmount', value)"
                />
              </label>
            </div>
          </FilterPopover>
        </FilterChip>
      </span>

      <button
        type="button"
        :aria-expanded="moreOpen"
        aria-controls="transaction-more-filters"
        class="flex h-[var(--density-control)] items-center gap-[var(--space-tight)] rounded-[var(--radius-control)] px-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        @click="moreOpen = !moreOpen"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
          class="shrink-0"
        >
          <path :d="moreOpen ? 'M3 8h10' : 'M3 8h10M8 3v10'" />
        </svg>
        {{ t('transactions.filters.moreFilters') }}
      </button>

      <button
        v-if="hasAnyFilter"
        type="button"
        class="h-[var(--density-control)] px-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] underline underline-offset-2 hover:text-[color:var(--color-text)]"
        @click="emit('clear')"
      >
        {{ t('transactions.filters.reset') }}
      </button>
    </div>
  </div>
</template>
